/**
 * ThemeService (SPEC §6): applies a theme to the editor chrome (CSS custom
 * properties) and to the viewport (`RenderBackend.applyTheme`) together, so
 * both always match.
 *
 * Switching, persistence and the `theme.set` command are added in the theming
 * slice. This file owns the mechanics of applying a theme.
 */
import type { ChangeScheduler } from "../../core/ChangeScheduler";
import { VersionedStore } from "../../core/store";
import type { ReadableStore } from "../../core/store";
import type { RenderBackend } from "../../render/RenderBackend";
import { defaultThemeFor, findTheme } from "../../themes";
import type { Theme } from "../../themes";
import { flattenThemeToCssVars } from "./flatten";

/** Where CSS variables go. The DOM implementation writes to `document.documentElement`. */
export interface ThemeTarget {
  setVariable(name: string, value: string): void;
  /** Mirrors the id into `data-theme`, matching the build's first-paint CSS. */
  setThemeId(id: string): void;
}

export interface ThemeServiceOptions {
  target: ThemeTarget;
  backend: RenderBackend;
  scheduler: ChangeScheduler;
  /** The viewport renders on demand, so a new theme needs a frame to show. */
  requestFrame: (reason: string) => void;
}

export class ThemeService {
  readonly current: ReadableStore<Theme>;
  private readonly store: VersionedStore<Theme>;
  private readonly target: ThemeTarget;
  private readonly backend: RenderBackend;
  private readonly requestFrame: (reason: string) => void;

  constructor(options: ThemeServiceOptions) {
    this.target = options.target;
    this.backend = options.backend;
    this.requestFrame = options.requestFrame;
    this.store = new VersionedStore(options.scheduler, defaultThemeFor("dark"));
    this.current = this.store;
  }

  apply(theme: Theme): void {
    for (const [name, value] of Object.entries(flattenThemeToCssVars(theme))) {
      this.target.setVariable(name, value);
    }
    this.target.setThemeId(theme.id);
    this.backend.applyTheme({
      mode: theme.mode,
      viewport: theme.viewport,
      axis: theme.axis,
      font: theme.font,
    });
    // Without this, an idle viewport keeps the previous theme's last frame.
    this.requestFrame("theme");
    this.store.publish(theme);
  }
}

export interface InitialThemeInput {
  /** The visitor's stored choice, if any. */
  readonly storedId: string | null;
  /** `prefers-color-scheme: light`. */
  readonly prefersLight: boolean;
}

/** The visitor's stored choice wins; otherwise follow the OS (SPEC §6). */
export function resolveInitialTheme(input: InitialThemeInput): Theme {
  const stored = input.storedId === null ? undefined : findTheme(input.storedId);
  return stored ?? defaultThemeFor(input.prefersLight ? "light" : "dark");
}

/** The DOM ThemeTarget: CSS variables and `data-theme` on the root element. */
export function documentThemeTarget(root: HTMLElement): ThemeTarget {
  return {
    setVariable: (name, value) => {
      root.style.setProperty(name, value);
    },
    setThemeId: (id) => {
      root.dataset.theme = id;
    },
  };
}
